import { Alert } from '@material-ui/lab';
import { ThemeProvider } from '@material-ui/core/styles';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Route, Switch } from 'react-router-dom';

import { Snackbars } from 'components/Snackbars';
import { Fund } from 'pages/detailsPages/Fund';
import { Company } from 'pages/detailsPages/Company';
import { PageRoute } from 'pages/PageRoute';
import { KPIRouter } from 'pages/KPIs/KPIRouter';
import { ScenariosPage } from 'pages/Scenarios';
import { routes } from 'routes';

import './App.css';
import CommonTheme from './CommonTheme';
import Auth from './components/Auth';
import { TimeTravelContextProvider } from './components/TimeTravel/context';
import { Footer } from './components/Footer';
import AuthTheme from './pages/Auth/AuthTheme';
import { LoginPage, PlatformIntegratedLoginPage } from './pages/Auth/Login';
import { PasswordResetPage } from './pages/Auth/PasswordReset';
import { PasswordResetConfirmPage } from './pages/Auth/PasswordResetConfirm';
import { PasswordResetLinkExpiredPage } from './pages/Auth/PasswordResetLinkExpired';

import { useQuery } from '@apollo/client';
import { USER } from './modules/user/queries';


/**
 * Just a javascript version of the react's `<Redirect to='/path' />`.
 * Given the `to` path, this changes the `window.location`.
 * 
 * Why is this needed?
 *    Note that some pages are server-rendered using `Django` and
 *    some are client-rendered using `react`.
 * 
 *    In server-rendered cases a regular `<Redirect to='/path' />` will 
 *    not work since the component works in a client-rendered way.
 */
const HardRedirect = (props: {to: string}) => (
  <Route path='*' render={() => window.location.href = props.to} />
)


const App = observer(() => {
  const queryClient = new QueryClient();

  const { loading, data } = useQuery(USER);
  const [userState, setUserState] = useState<any>();
  const { navigationPermissions = {} } = userState || {}

  // navigation togglers
  const showFundsPage = (navigationPermissions.isSiteModeDefault || navigationPermissions.isLimitedPartner)
  const showCompaniesPage = navigationPermissions.isCompanyPageAccessible
  const showScenariosPage = (navigationPermissions.isSiteModeDefault)
  const showKPIsPage = (navigationPermissions.isSiteModeDefault)

  useEffect(() => {
    if (!loading && data && data.currentUser) {
      setUserState(data.currentUser);
    }
  }, [loading, data]);

  // Toggle which LoginPage to render based on the flippers config
  const FlipperBasedLoginPage = window?.GLOBAL_CONFIG?.FLIPPERS
    ?.PLATFORM_INTEGRATED_LOGIN_PAGE
    ? PlatformIntegratedLoginPage
    : LoginPage;

  return (
    <Switch>

      <Route path={routes.LOGIN} exact render={(props) =>
        <ThemeProvider theme={AuthTheme}>
            <FlipperBasedLoginPage query={props.location.search} />
        </ThemeProvider>
      } />

      <Route path={routes.PASSWORD_RESET} exact>
        <ThemeProvider theme={AuthTheme}>
          <PasswordResetPage />
        </ThemeProvider>
      </Route>

      <Route path={routes.PASSWORD_RESET_LINK_EXPIRED} exact>
        <ThemeProvider theme={AuthTheme}>
          <PasswordResetLinkExpiredPage />
        </ThemeProvider>
      </Route>

      <Route path={routes.PASSWORD_RESET_CONFIRM} exact>
        <ThemeProvider theme={AuthTheme}>
          <PasswordResetConfirmPage />
        </ThemeProvider>
      </Route>

      <Route>
        <ThemeProvider theme={CommonTheme}>
            {/*@ts-ignore*/}
            <Auth user={userState}>
              {window.GLOBAL_CONFIG.HIJACKED &&
                <Alert
                  severity="warning"
                  classes={{message: 'container pl-3'}}
                  icon={false}
                >
                  You are viewing site as {window.GLOBAL_CONFIG.HIJACKED}. <a href="/hijack/disable/">Return to normal view</a>
                </Alert>
              }
              <Snackbars />
              <QueryClientProvider client={queryClient}>
                <Switch>

                  {showScenariosPage && <PageRoute
                    exact
                    path={routes.FUND_SCENARIOS}
                    PageComponent={ScenariosPage}
                    graphqlUrl={window.GLOBAL_CONFIG.GRAPHQL_URL}
                  />}

                  {showKPIsPage && <PageRoute
                    path={routes.KPI}
                    PageComponent={KPIRouter}
                    graphqlUrl={window.GLOBAL_CONFIG.API_GATEWAY_GRAPHQL_URL}
                  />}

                  <TimeTravelContextProvider>
                    <Switch>

                      {showFundsPage && <Route exact path={routes.FUND_ROOT}><Fund user={userState}/></Route>}
                      {showFundsPage && <Route exact path={routes.FUND}><Fund user={userState}/></Route>}
                      {showCompaniesPage && <Route exact path={routes.COMPANY_ROOT}><Company/></Route>}
                      {showCompaniesPage && <Route exact path={routes.COMPANY}><Company/></Route>}

                      <HardRedirect to="/" />

                    </Switch>
                  </TimeTravelContextProvider>

                  <HardRedirect to="/" />

                </Switch>
              </QueryClientProvider>
              <Footer />
            </Auth>
        </ThemeProvider>
      </Route>

      <HardRedirect to="/" />

    </Switch>
  );
});

export default App;
